import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { LeaseContract, LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum } from "@/api/generated";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import { CustomFormLabel } from "@/components/property_edit/PropertyEditInputs";
import { CustomSelector } from "@/components/property_edit/PropertyEditInputs";
import { DatePickerComponent } from "@/components/property_edit/PropertyEditInputs";
import { LeaseContractTypeOfTheIncomeOfTheBeginningOfContractOptions } from "@/constants/management";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { createLeaseContractHistorySchema, LeaseContractHistoryFormData } from "@/validations/management/edit";
import { useRequestInitialLoading, PropertyManagementDetailSnackbarState } from "@/view_models/atoms";
import { SelectedProperty } from "@/view_models/propertySelectors";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

interface props {
  leaseContract?: LeaseContract;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
}

// 履歴編集用コンポーネント
export const LeaseContractHistoryEditor: React.FC<props> = ({ leaseContract, onCancel, onSave, onCreate }) => {
  const classes = useStyles();

  const schema = createLeaseContractHistorySchema();

  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
  } = useForm<LeaseContractHistoryFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      starts_at: leaseContract?.starts_at ? dayjs(leaseContract.starts_at).toDate() : new Date(),
      starts_at_day: leaseContract?.starts_at ? leaseContract.starts_at.split("-")[2] : "1",
      ends_at: leaseContract?.ends_at ? dayjs(leaseContract.ends_at).toDate() : new Date(),
      ends_at_day: leaseContract?.ends_at ? leaseContract.ends_at.split("-")[2] : "1",
      monthly_fee: leaseContract?.monthly_fee?.toString() || "",
      monthly_management_fee: leaseContract?.monthly_management_fee?.toString() || "",
      other_monthly_income: leaseContract?.other_monthly_income?.toString() || "",
      type_of_the_income_of_the_beginning_of_contract:
        leaseContract?.type_of_the_income_of_the_beginning_of_contract || "",
      security_deposit: leaseContract?.security_deposit?.toString() || "",
      key_money: leaseContract?.key_money?.toString() || "",
      outsourcing_fee_rate_of_new_lease_contract:
        leaseContract?.outsourcing_fee_rate_of_new_lease_contract?.toString() || "",
      outsourcing_fee_rate_of_updating_lease_contract:
        leaseContract?.outsourcing_fee_rate_of_updating_lease_contract?.toString() || "",
    },
  });
  const history = useHistory();
  const requestInitialLoading = useRequestInitialLoading();
  // 選択済み物件取得
  const selected_property = useRecoilValue(SelectedProperty);
  // 物件詳細でのスナックバー表示
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);

  const watchValues = watch(["type_of_the_income_of_the_beginning_of_contract", "starts_at", "starts_at_day"]);

  const updateEndsAt = (startsAt: Date | string, startsAtDay: string) => {
    if (startsAt && startsAtDay) {
      const startsAtDate = dayjs(startsAt);
      const startDate = startsAtDate.date(parseInt(startsAtDay));
      const endDate = startDate.add(2, "year").subtract(1, "day");

      setValue("ends_at", endDate.toDate());
      setValue("ends_at_day", endDate.format("D"));
    }
  };

  const onSubmit = async (data: LeaseContractHistoryFormData) => {
    const fp = PropallyApiFactory();
    const { type_of_the_income_of_the_beginning_of_contract, ...restValues } = data;
    const sendData: LeaseContract = {
      ...restValues,
      starts_at: `${dayjs(data.starts_at).format("YYYY-MM")}-${data.starts_at_day}`,
      ends_at: `${dayjs(data.ends_at).format("YYYY-MM")}-${data.ends_at_day}`,
      monthly_fee: parseFormattedNumber(data.monthly_fee),
      monthly_management_fee: parseFormattedNumber(data.monthly_management_fee),
      other_monthly_income: parseFormattedNumber(data.other_monthly_income),
      security_deposit: data.security_deposit ? parseFormattedNumber(data.security_deposit) : undefined,
      key_money: data.key_money ? parseFormattedNumber(data.key_money) : undefined,
      outsourcing_fee_rate_of_new_lease_contract: data.outsourcing_fee_rate_of_new_lease_contract
        ? parseFormattedNumber(data.outsourcing_fee_rate_of_new_lease_contract)
        : undefined,
      outsourcing_fee_rate_of_updating_lease_contract: data.outsourcing_fee_rate_of_updating_lease_contract
        ? parseFormattedNumber(data.outsourcing_fee_rate_of_updating_lease_contract)
        : undefined,
    };
    if (type_of_the_income_of_the_beginning_of_contract) {
      sendData.type_of_the_income_of_the_beginning_of_contract =
        type_of_the_income_of_the_beginning_of_contract as LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum;
    }

    try {
      // idがある場合は更新、ない場合は新規作成
      // 更新
      if (leaseContract?.id) {
        const result = await fp.v1PropertiesPropertyIdLeaseContractsLeaseContractIdPatch(
          selected_property.id,
          leaseContract?.id,
          sendData,
          {
            withCredentials: true,
          }
        );
        if (result.status === 200) {
          requestInitialLoading();
        }
      } else {
        // 新規作成
        const result = await fp.v1PropertiesPropertyIdLeaseContractsPost(selected_property.id, sendData, {
          withCredentials: true,
        });
        if (result.status === 200) {
          requestInitialLoading();
        }
      }

      // APIの処理が成功した時に実行する。
      if (onSave) {
        onSave();
      } else if (onCreate) {
        onCreate();
      }
    } catch (e) {
      console.error(e);
      setManagementDetailSnackBarState({
        message:
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "物件情報を変更できませんでした",
        status: "error",
        isOpen: true,
      });
      if (selected_property.property.property_type === PROPERTY_TYPE_VALUES.consideration) {
        history.push("/management/consideration/detail");
      } else {
        history.push("/management/own/detail");
      }
    }
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit, (errors) => console.log(errors))}>
        <Box mb={2}>
          <Box mb={1}>
            <CustomFormLabel
              label="賃貸借契約の開始月"
              required
              info="購入した年月日を選択してください。"
              helperText="※一棟の場合は直近で契約更新した部屋の期間を入力"
            />
          </Box>

          <Typography component="h3" data-edit-section>
            契約期間
          </Typography>
          <Controller
            name="starts_at"
            control={control}
            render={({ ...field }) => (
              <DatePickerComponent
                {...field}
                labelLight="年月"
                onChange={(date) => {
                  field.onChange(date);
                  updateEndsAt(date, watchValues.starts_at_day);
                }}
              />
            )}
          />

          <Controller
            name="starts_at_day"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                labelLight="日にち"
                helperText="不明の場合は値を「1日」のままお進みください。"
                error={!!errors.starts_at_day}
                errorText={errors.starts_at_day?.message}
                onChange={(e) => {
                  const inputDate = e.target.value;
                  field.onChange(inputDate);
                  updateEndsAt(watchValues.starts_at, inputDate);
                }}
                placeHolder="1"
                unitSuffix="日"
                isShort
              />
            )}
          />

          <Box mb={1}>
            <CustomFormLabel label="賃貸借契約の終了月" required info="購入した年月日を選択してください。" />
          </Box>

          <Controller
            name="ends_at"
            control={control}
            render={({ ...field }) => (
              <DatePickerComponent
                {...field}
                labelLight="年月"
                error={!!errors.ends_at}
                errorText={errors.ends_at?.message}
              />
            )}
          />
          <Controller
            name="ends_at_day"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                labelLight="日にち"
                helperText="不明の場合は値を「1日」のままお進みください。"
                error={!!errors.ends_at_day}
                errorText={errors.ends_at_day?.message}
                onChange={field.onChange}
                placeHolder="1"
                unitSuffix="日"
                isShort
              />
            )}
          />

          <Typography component="h3" data-edit-section>
            毎月の収入
          </Typography>
          <Controller
            name="monthly_fee"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="家賃収入"
                required
                error={!!errors.monthly_fee}
                errorText={errors.monthly_fee?.message}
                onChange={field.onChange}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />
          <Controller
            name="monthly_management_fee"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="管理費収入"
                required
                error={!!errors.monthly_management_fee}
                errorText={errors.monthly_management_fee?.message}
                onChange={field.onChange}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />
          <Controller
            name="other_monthly_income"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="その他収入（駐車場など）"
                required
                error={!!errors.other_monthly_income}
                errorText={errors.other_monthly_income?.message}
                onChange={field.onChange}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />
          <Typography component="h3" data-edit-section>
            契約開始時の収入と支出
          </Typography>
          <Controller
            name="type_of_the_income_of_the_beginning_of_contract"
            control={control}
            render={({ ...field }) => (
              <CustomSelector
                {...field}
                label="賃貸借契約開始時における収入"
                onChange={field.onChange}
                error={!!errors.type_of_the_income_of_the_beginning_of_contract}
                errorText={errors.type_of_the_income_of_the_beginning_of_contract?.message}
                options={LeaseContractTypeOfTheIncomeOfTheBeginningOfContractOptions}
              />
            )}
          />
          {/* security_deposit */}
          {watchValues.type_of_the_income_of_the_beginning_of_contract ===
            LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.SecurityDeposit && (
            <Controller
              name="security_deposit"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="更新料"
                  required
                  error={!!errors.security_deposit}
                  errorText={errors.security_deposit?.message}
                  onChange={field.onChange}
                  placeHolder="1"
                  unitSuffix="ヶ月分"
                />
              )}
            />
          )}
          {/* 礼金 */}
          {watchValues.type_of_the_income_of_the_beginning_of_contract ===
            LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.KeyMoney && (
            <Controller
              name="key_money"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="礼金"
                  required
                  error={!!errors.key_money}
                  errorText={errors.key_money?.message}
                  onChange={field.onChange}
                  placeHolder="1"
                  unitSuffix="ヶ月分"
                />
              )}
            />
          )}

          {(watchValues.type_of_the_income_of_the_beginning_of_contract ===
            LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.KeyMoney ||
            watchValues.type_of_the_income_of_the_beginning_of_contract ===
              LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.SecurityDeposit) && (
            <Box mt={2}>
              <CustomFormLabel label="賃貸借契約開始時における支出" />

              {/* 新規賃貸契約手数料 */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.KeyMoney && (
                <Controller
                  name="outsourcing_fee_rate_of_new_lease_contract"
                  control={control}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="新規賃貸契約手数料"
                      required
                      error={!!errors.outsourcing_fee_rate_of_new_lease_contract}
                      errorText={errors.outsourcing_fee_rate_of_new_lease_contract?.message}
                      onChange={field.onChange}
                      placeHolder="0.5"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}

              {/* 賃貸契約更新時手数料 */}
              {watchValues.type_of_the_income_of_the_beginning_of_contract ===
                LeaseContractTypeOfTheIncomeOfTheBeginningOfContractEnum.SecurityDeposit && (
                <Controller
                  name="outsourcing_fee_rate_of_updating_lease_contract"
                  control={control}
                  render={({ ...field }) => (
                    <CustomForm
                      {...field}
                      label="賃貸契約更新時手数料"
                      required
                      error={!!errors.outsourcing_fee_rate_of_updating_lease_contract}
                      errorText={errors.outsourcing_fee_rate_of_updating_lease_contract?.message}
                      onChange={field.onChange}
                      placeHolder="0.5"
                      unitSuffix="ヶ月分"
                    />
                  )}
                />
              )}
            </Box>
          )}
        </Box>

        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" size="small" fullWidth>
              保存
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
